/**
 * @module lib/commercial/keytar/Meter
 */
import { local } from "lib/store";
import logger from "utils/logger";
import addDays from "date-fns/add_days";
import compareAsc from "date-fns/compare_asc";

const debugTag = "[METER]";

/**
 * Create a meter used to track the users' page-views.
 * @class Meter
 */
export default class Meter {
  /**
   * Namespace a key label for localStorage use.
   * @static
   * @param {String} key - to be prepended with constant `tgam.keytar.[key]` namespace
   * @returns {String} of name-spaced key label
   */
  static namespaceKey(key) {
    return `tgam.keytar.${key}`;
  }

  /**
   * Creates an instance of Meter
   * @param {Object} c - collection or properties
   * @param {String} [c.key] - used for local storage
   * @param {Number} [c.limit] - of the amount of page-views the meter will allow a user, before it expires.
   * @param {Number} [c.renew] - of the amount of days after which the meter count should reset.
   * @param {Date} [c.now=new Date()] - current date instance
   */
  constructor({ key, limit, renew, now = new Date() } = {}) {
    if (!key || !limit || !renew) {
      throw new Error("Meter class constructor requires a locaStorage key name, limit, and renew days.");
    }

    this.key = Meter.namespaceKey(key);
    this.limit = limit;
    this.now = now;
    this.retrieve("count", "0");

    const compare = addDays(this.created, renew);

    // compareAsc() compares the dates and returns:
    // -1 = the first date is before the second
    //  0 = dates are equal
    //  1 = the first date is after the second
    if (compareAsc(now, compare) >= 0) {
      logger.info(`${debugTag} reset:`, this.created, now, renew, compare);
      this.reset();
    }
  }

  /**
   * Retrieve the local storage value
   * @private
   * @param {String} id - appended to `this.key` for local storage lookup
   * @param {String} def - value to return if not present
   * @returns {String} of local storage key value
   */
  retrieve(id, def) {
    try {
      const value = local.get(`${this.key}.${id}`);
      if (value) {
        return value;
      } else {
        this.store(id, def);
        return def;
      }
    } catch (e) {
      return def;
    }
  }

  /**
   * Set an associated local storage value
   * @private
   * @param {String} id - appended to `this.key` for local storage lookup
   * @param {String} value - to save into local storage
   */
  store(id, value) {
    local.set(`${this.key}.${id}`, value);
  }

  /**
   * Remove an associated local storage value
   * @private
   * @param {String} id - appended to `this.key` for local storage lookup
   */
  remove(id) {
    local.remove(`${this.key}.${id}`);
  }

  /**
   * Get the meter count value (number of page-views).
   * @readonly
   * @returns {Number} of meter count
   */
  get count() {
    return parseInt(this.retrieve("count", "0"), 10);
  }

  /**
   * Get the date when the meter was created.
   * @readonly
   * @returns {String} of creation date (ISO 8601 format)
   */
  get created() {
    return this.retrieve("created", this.now.toISOString());
  }

  /**
   * Get the free articles for this meter.
   * Which the user viewed before their limit was up.
   * @readonly
   * @returns {Array<String>} of article urls
   */
  get free() {
    try {
      return JSON.parse(this.retrieve("free", "[]"));
    } catch (e) {
      return [];
    }
  }

  /**
   * Increment the meter count every time, and try to add the contentId to the free samples list.
   * @param {String} contentId - of the article
   */
  increment(contentId) {
    const newCount = this.count + 1;
    this.store("count", newCount.toString());

    // Check the actual length of the array, instead of the count.
    const samples = this.free; // articles
    if (samples.length < this.limit) {
      samples.push(contentId); // Add a new free article to the list
      logger.info(`${debugTag} new free:`, contentId);
      this.store("free", JSON.stringify(samples));
    }
  }

  /**
   * Log the meter count and limit, along with the free article contentIds.
   * Similar to the logging found in increment(), and both won't be called at the same time.
   */
  log() {
    logger.info(`${debugTag} free:`, this.free.length, "- limit:", this.limit, "- count:", this.count);
  }

  /**
   * Determine if the free article count is greater than or equal to the meter limit.
   * @readonly
   * @returns {Boolean} true when meter is full / expired
   */
  get full() {
    return this.free.length >= this.limit;
  }

  /**
   * Remove the created and count to default values
   */
  reset() {
    ["created", "count", "free"].forEach(i => this.remove(i));
  }

  /**
   * Check if the passed article (contentId) was read by the user, before the meter expired.
   * According to the local storage item for this meter.
   * Don't include the location hash or search values.
   * @param {String} contentId - to be checked
   * @returns {Boolean} true when article has been read
   */
  isFreeArticle(contentId) {
    const found = this.free.find(art => art === contentId);
    if (found) {
      logger.info(`${debugTag} found free:`, found);
    }
    return found ? true : false;
  }
}
